import { createContext, useCallback, useContext } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import {
  InlineSkillApi,
  InlineSkillApiList,
  InlineSkillCreateApi,
} from './InlineSkillApiProviderTypes';

interface InlineSkillApiContextType {
  getInlineSkills: (skip?: number, limit?: number, search?: string) => Promise<InlineSkillApiList>;
  getInlineSkillByKey: (inlineSkillKey: string) => Promise<InlineSkillApi>;
  createInlineSkill: (inlineSkill: InlineSkillCreateApi) => Promise<InlineSkillApi>;
  updateInlineSkill: (
    inlineSkillKey: string,
    inlineSkill: InlineSkillCreateApi,
  ) => Promise<InlineSkillApi>;
  deleteInlineSkill: (inlineSkillKey: string) => Promise<void>;
}

const InlineSkillApiContext = createContext<InlineSkillApiContextType | null>(null);

export function InlineSkillApiProvider({ children }: { children: React.ReactNode }) {
  const { get, put, del } = useApiService(CONFIG.SERVER_ASSISTANT);

  const getInlineSkills = useCallback(
    async (skip: number = 0, limit: number = 10, search?: string): Promise<InlineSkillApiList> => {
      try {
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
        const response = await get(`/v1/inline_skills?skip=${skip}&limit=${limit}${searchParam}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching inline skills:', error);
        throw error;
      }
    },
    [get],
  );

  const getInlineSkillByKey = useCallback(
    async (inlineSkillKey: string): Promise<InlineSkillApi> => {
      try {
        const response = await get(`/v1/inline_skills/${inlineSkillKey}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching inline skill:', error);
        throw error;
      }
    },
    [get],
  );

  const createInlineSkill = useCallback(
    async (inlineSkill: InlineSkillCreateApi): Promise<InlineSkillApi> => {
      try {
        const response = await put(`/v1/inline_skills`, inlineSkill);
        return response.data;
      } catch (error) {
        console.error('Error creating inline skill:', error);
        throw error;
      }
    },
    [put],
  );

  const updateInlineSkill = useCallback(
    async (inlineSkillKey: string, inlineSkill: InlineSkillCreateApi): Promise<InlineSkillApi> => {
      try {
        const response = await put(`/v1/inline_skills/${inlineSkillKey}`, inlineSkill);
        return response.data;
      } catch (error) {
        console.error('Error updating inline skill:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteInlineSkill = useCallback(
    async (inlineSkillKey: string): Promise<void> => {
      try {
        const response = await del(`/v1/inline_skills/${inlineSkillKey}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting inline skill:', error);
        throw error;
      }
    },
    [del],
  );

  return (
    <InlineSkillApiContext.Provider
      value={{
        getInlineSkills,
        getInlineSkillByKey,
        createInlineSkill,
        updateInlineSkill,
        deleteInlineSkill,
      }}
    >
      {children}
    </InlineSkillApiContext.Provider>
  );
}

export function useInlineSkillApi(): InlineSkillApiContextType {
  const context = useContext(InlineSkillApiContext);
  if (!context) {
    throw new Error('useInlineSkillApi must be used within an InlineSkillApiProvider');
  }
  return context;
}
